import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import serviceLogo from '../assets/images/service_logo.png'; // swap to your ops logo if needed
import 'react-toastify/dist/ReactToastify.css';

const TechOpReport = () => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [opReport, setOpReport] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${apiUrl}/operation_crud.php?getOpReport=${id}`,
          { headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' } }
        );
        // Accept both ['success', payload] and {success:true, data:payload}
        let payload = null;
        if (Array.isArray(data) && data[0] === 'success') payload = data[1];
        else if (data?.success) payload = data.data;

        if (!payload) {
          toast.error(data?.error || 'No data available', { position: 'top-center' });
          return;
        }
        setOpReport(payload);
      } catch (err) {
        if (err.response?.status === 401) {
          toast.error('Session expired. Please login again.', { position: 'top-center' });
          navigate('/login');
        } else if (err.response?.status === 403) {
          toast.error("Oops!! you don't have access to this area. Speak to the CFO.", { position: 'top-center' });
        } else {
          toast.error('Error loading report', { position: 'top-center' });
        }
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [apiUrl, navigate, id]);

  const handlePrint = () => window.print();

  if (loading) return <div className="p-5">Loading...</div>;
  if (!opReport) return <div className="p-5">No data available</div>;

  return (
    <div className="mx-auto bg-white p-6" style={{ width: '8.5in', minHeight: '11in' }}>
      {/* Print Button */}
      <div className="mb-4 print:hidden">
        <button
          onClick={handlePrint}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Print
        </button>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          .menu { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          table { width: 100%; border-collapse: collapse; page-break-inside: avoid; table-layout: fixed; }
          th, td { border: 1px solid #000; padding: 6px; font-size: 12px; word-wrap: break-word; }
          * { color: #000 !important; }
          @page { size: Letter; margin: 0.5in; }
        }
      `}</style>

      <h3 className="text-right text-gray-500 text-xl mb-4">Operation Report #{opReport?.id}</h3>

      {/* Header / Customer */}
      <div className="bg-white px-4 py-5 mb-4 border border-gray-300 rounded-md">
        <div className="flex justify-between items-start">
          <div>
            <img src={serviceLogo} alt="logo" className="max-w-96" />
          </div>

          <div className="flex-1 ml-auto">
            <div className="min-w-0">
              {/* <div className="flex items-center">
                <p className="text-sm font-semibold text-gray-900 mr-2">Customer:</p>
                <p className="text-sm text-gray-700">{opReport.customer_name || '-'}</p>
              </div>

              <div className="flex items-center mt-2">
                <p className="text-sm font-semibold text-gray-900 mr-2">Address:</p>
                <p className="text-sm text-gray-700">{opReport.job_site || '-'}</p>
              </div> */}

              {/* {opReport.project_name && (
                <div className="flex items-center mt-2">
                  <p className="text-sm font-semibold text-gray-900 mr-2">Project:</p>
                  <p className="text-sm text-gray-700">{opReport.project_name}</p>
                </div>
              )} */}
            </div>
          </div>
        </div>
      </div>

      {/* Schedule / Meta */}
      <div className="flex flex-wrap gap-4 mb-3">
        <div className="flex-grow flex items-center border border-gray-300 p-2">
          <div className="font-bold mr-2">Schedule From:</div>
          <div>{opReport.schedule_from || '-'}</div>
        </div>
        <div className="flex-grow flex items-center border border-gray-300 p-2">
          <div className="font-bold mr-2">Schedule To:</div>
          <div>{opReport.schedule_to || '-'}</div>
        </div>
        <div className="flex-grow flex items-center border border-gray-300 p-2">
          <div className="font-bold mr-2">Start Time:</div>
          <div>{opReport.start_time || '-'}</div>
        </div>
        <div className="flex-grow flex items-center border border-gray-300 p-2">
          <div className="font-bold mr-2">End Time:</div>
          <div>{opReport.end_time || '-'}</div>
        </div>
        <div className="flex-grow flex items-center border border-gray-300 p-2">
          <div className="font-bold mr-2">Status:</div>
          <div className={opReport.status === 'Completed' ? 'text-green-600' : 'text-orange-700'}>
            {opReport.status || '-'}
          </div>
        </div>
        <div className="flex-grow flex items-center border border-gray-300 p-2">
          <div className="font-bold mr-2">Technician:</div>
          <div>{opReport.tech_name || '-'}</div>
        </div>
      </div>

      {/* Description (NEW COLUMN) */}
      <div className="border border-black rounded-md p-4 mb-6">
        <div className="font-bold underline mb-2">Operation Description:</div>
        <div className="text-sm">{opReport.description || '-'}</div>
      </div>

      {/* Technician Notes */}
      {opReport.tech_notes && (
        <div className="border border-black rounded-md p-4 mb-6">
          <div className="font-bold underline mb-2">Technician Notes:</div>
          <div className="text-sm whitespace-pre-wrap">{opReport.tech_notes}</div>
        </div>
      )}

      {/* Coordinator Comment */}
      {opReport.user_comment && (
        <div className="border border-black rounded-md p-4 mb-6">
          <div className="font-bold underline mb-2">Coordinator Comment:</div>
          <div className="text-sm">{opReport.user_comment}</div>
        </div>
      )}

      {/* Time Log (if you keep it for audit)
      {opReport.time_log && (
        <div className="border border-gray-300 rounded-md p-4">
          <div className="font-bold mb-1">Logged:</div>
          <div className="text-sm text-gray-700">{opReport.date} {opReport.time_log}</div>
        </div>
      )} */}
    </div>
  );
};

export default TechOpReport;
